import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../lib/api/client';
import { PLACEHOLDER_FELLOWS, LEVEL_LABELS } from '../lib/fellowshipConstants';
import '../styles/fellow-scroll-wall.css';

// See FellowOfTheWeekCard.js — same production-safe base, same bug fixed.
const API_BASE = API_BASE_URL;

/* Warm, rich placeholder backgrounds (one per fellow, cycles) */
const PLACEHOLDER_BG = [
  'linear-gradient(145deg, #C45A1A 0%, #8A2E08 100%)',   // deep rust
  'linear-gradient(145deg, #6B3FA0 0%, #3D1A6E 100%)',   // deep plum
  'linear-gradient(145deg, #0D6B5C 0%, #083D34 100%)',   // deep teal
  'linear-gradient(145deg, #B8860B 0%, #7A5600 100%)',   // deep gold
  'linear-gradient(145deg, #8B2252 0%, #5C1035 100%)',   // deep rose
  'linear-gradient(145deg, #1A5080 0%, #0D3050 100%)',   // deep ocean
  'linear-gradient(145deg, #556B2F 0%, #2E3D18 100%)',   // deep olive
  'linear-gradient(145deg, #8B4513 0%, #5C2E0A 100%)',   // deep sienna
  'linear-gradient(145deg, #4A1942 0%, #2C0D28 100%)',   // deep violet
];

/* Level display config */
const LEVEL_COLORS = {
  associate:            '#8C929E',
  fellow:               '#9AABB8',
  senior_fellow:        '#C8880A',
  distinguished_fellow: '#D4962A',
};

function FellowCard({ fellow, bgIndex }) {
  const levelColor = LEVEL_COLORS[fellow.level] || '#C8800A';
  const levelLabel = LEVEL_LABELS[fellow.level] || fellow.level;
  const placeholderBg = PLACEHOLDER_BG[bgIndex % PLACEHOLDER_BG.length];

  return (
    <div className="fsw-card">
      {/* Photo area — landscape on top */}
      <div className="fsw-photo-wrap">
        {fellow.image_url ? (
          <img
            src={fellow.image_url?.startsWith('http') ? fellow.image_url : `${API_BASE}${fellow.image_url}`}
            alt={fellow.name}
            className="fsw-photo"
            loading="lazy"
          />
        ) : (
          <div className="fsw-photo-placeholder" style={{ background: placeholderBg }}>
            {fellow.name.charAt(0)}
          </div>
        )}
        {/* Level badge on photo */}
        <span className="fsw-badge" style={{ color: levelColor }}>
          {levelLabel}
        </span>
      </div>

      {/* White text section below photo */}
      <div className="fsw-body">
        <p className="fsw-name">{fellow.name}</p>
        {fellow.title       && <p className="fsw-title">{fellow.title}</p>}
        {fellow.institution && <p className="fsw-inst">{fellow.institution}</p>}
      </div>
    </div>
  );
}

function ScrollColumn({ fellows, direction, speed, startOffset = 0, delay = '0s' }) {
  const items = [...fellows, ...fellows];
  return (
    <div className="fsw-column">
      <div
        className={`fsw-track fsw-track--${direction}`}
        style={{ animationDuration: `${speed}s`, animationDelay: delay }}
      >
        {items.map((f, i) => (
          <FellowCard
            key={`${f.id || f.name}-${i}`}
            fellow={f}
            bgIndex={startOffset + i}
          />
        ))}
      </div>
    </div>
  );
}

export default function FellowScrollWall() {
  const [fellows, setFellows] = useState([]);
  const [loaded, setLoaded]   = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/public/fellows`)
      .then(r => r.json())
      .then(data => {
        setFellows(data.fellows?.length > 0 ? data.fellows : PLACEHOLDER_FELLOWS);
      })
      .catch(() => setFellows(PLACEHOLDER_FELLOWS))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) {
    return (
      <div className="fsw-root fsw-loading">
        <div className="fsw-shimmer-col" />
        <div className="fsw-shimmer-col" />
        <div className="fsw-shimmer-col" />
      </div>
    );
  }

  /* Pad a column to at least `min` items by cycling through its own items.
     Falls back to the full fellows list if the column is empty. */
  function fillCol(arr, min = 6) {
    const src = arr.length > 0 ? arr : fellows;
    const result = [...src];
    while (result.length < min) result.push(...src);
    return result.slice(0, min);
  }

  const col1 = fillCol(fellows.filter((_, i) => i % 3 === 0));
  const col2 = fillCol(fellows.filter((_, i) => i % 3 === 1));
  const col3 = fillCol(fellows.filter((_, i) => i % 3 === 2));

  return (
    <div className="fsw-root" aria-hidden="true">
      <div className="fsw-fade-top"    />
      <div className="fsw-fade-bottom" />
      <ScrollColumn fellows={col1} direction="up"   speed={26} startOffset={0} />
      <ScrollColumn fellows={col2} direction="down" speed={32} startOffset={3} />
      <ScrollColumn fellows={col3} direction="up"   speed={20} startOffset={6} />
    </div>
  );
}
